import { useEffect, useRef, type CSSProperties } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ChecklistPhase } from '../../types';

interface GrowthRoadmapProps {
    phases: ChecklistPhase[];
    selectedPhaseId: number;
    currentPhaseId?: number;
    doneCounts: Record<number, number>;
    totalCounts: Record<number, number>;
    onSelectPhase: (phaseId: number) => void;
    t: (key: string) => string;
    /** Strong phase accent (Figma `color`); soft fill derived via CSS --phase-accent-soft */
    phaseAccent?: string;
}

const GrowthRoadmap = ({
    phases,
    selectedPhaseId,
    currentPhaseId,
    doneCounts,
    totalCounts,
    onSelectPhase,
    t,
    phaseAccent,
}: GrowthRoadmapProps) => {
    const tr = (key: string) => t(key);
    const scrollRef = useRef<HTMLDivElement>(null);

    const selectedIndex = phases.findIndex((p) => p.id === selectedPhaseId);
    const selectedPhase = selectedIndex >= 0 ? phases[selectedIndex] : undefined;

    const goPrev = () => {
        if (selectedIndex > 0) onSelectPhase(phases[selectedIndex - 1].id);
    };
    const goNext = () => {
        if (selectedIndex >= 0 && selectedIndex < phases.length - 1)
            onSelectPhase(phases[selectedIndex + 1].id);
    };

    useEffect(() => {
        const root = scrollRef.current;
        if (!root || selectedIndex < 0) return;
        const chip = root.children[selectedIndex] as HTMLElement | undefined;
        chip?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, [selectedIndex, selectedPhaseId]);

    const accent = phaseAccent || 'var(--phase-accent, var(--accent-color))';
    const phaseTitle =
        selectedPhase &&
        (selectedPhase.minAgeMonths < 0
            ? tr('page.myrebaby.phase.pregnancy')
            : `${selectedPhase.minAgeMonths}–${selectedPhase.maxAgeMonths}m`);

    /**
     * Round nav — cream fill, accent ring, highlight chevron.
     * Inline styles beat `.shop-root button { border: 1px solid transparent }` in shop.css
     * (that rule was wiping the ring and making arrows look missing).
     */
    const navBtnStyle = (disabled: boolean): CSSProperties => ({
        width: 44,
        height: 44,
        minWidth: 44,
        minHeight: 44,
        padding: 0,
        borderRadius: 9999,
        border: '2px solid var(--accent-color)',
        backgroundColor: 'color-mix(in srgb, var(--bg-color), white 55%)',
        color: 'var(--highlight-color)',
        boxShadow: '0 1px 2px color-mix(in srgb, var(--text-color), transparent 88%)',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    });

    return (
        <div className="w-full space-y-4">
            {/*
              Full-width row: chips grow evenly (flex-1) but never below min-w so
              "48–60m" / "Zwangerschap" stay on one line. Extra phases scroll on
              narrow phones instead of mid-label wraps.
            */}
            <div
                ref={scrollRef}
                className="flex w-full items-stretch gap-2 overflow-x-auto pb-1 scrollbar-none sm:gap-2.5"
            >
                {phases.map((phase) => {
                    const isSelected = phase.id === selectedPhaseId;
                    const isCurrent = phase.id === currentPhaseId;
                    const strong = phase.content?.accentColor || accent;
                    const soft = `color-mix(in srgb, ${strong} 16%, white)`;
                    const done = doneCounts[phase.id] ?? 0;
                    const total = totalCounts[phase.id] ?? 0;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                    const isPregnancy = phase.minAgeMonths < 0;
                    const label = isPregnancy
                        ? tr('page.myrebaby.phase.pregnancy')
                        : `${phase.minAgeMonths}–${phase.maxAgeMonths}m`;

                    // Inline layout beats `.shop-root button { padding; border-radius }` so long
                    // labels like "Zwangerschap" are not squeezed into broken lines.
                    const chipStyle: CSSProperties = {
                        padding: '0.625rem 0.55rem 0.5rem',
                        borderRadius: '0.75rem',
                        // Never shrink below content; grow when the row has room
                        flex: '1 0 auto',
                        minWidth: isPregnancy ? 'max-content' : '4.75rem',
                        width: 'auto',
                        ...(isSelected
                            ? {
                                  backgroundColor: soft,
                                  boxShadow: `inset 0 -4px 0 0 ${strong}`,
                                  border: '1px solid transparent',
                              }
                            : {
                                  backgroundColor:
                                      'color-mix(in srgb, var(--bg-color), white 50%)',
                                  border:
                                      '1px solid color-mix(in srgb, var(--text-color), transparent 88%)',
                              }),
                    };

                    return (
                        <button
                            key={phase.id}
                            type="button"
                            onClick={() => onSelectPhase(phase.id)}
                            title={label}
                            className="flex min-h-[5.75rem] flex-col items-center justify-between gap-1.5 text-[11px] leading-none transition hover:opacity-90 sm:text-xs"
                            style={chipStyle}
                        >
                            <span className="relative text-2xl leading-none pt-0.5">
                                {phase.icon ?? '👶'}
                                {isCurrent && (
                                    <span className="absolute -top-1.5 -right-2.5 text-[8px] px-1 rounded bg-[var(--text-color)] text-[var(--bg-color)] font-bold leading-none py-0.5">
                                        {tr('page.myrebaby.now')}
                                    </span>
                                )}
                            </span>
                            {/* Single line always — age ranges + pregnancy (nl Zwangerschap) */}
                            <span className="px-0.5 text-center font-semibold text-[var(--text-color)] whitespace-nowrap">
                                {label}
                            </span>
                            <span
                                className="mt-auto h-1.5 w-full min-w-[2.5rem] overflow-hidden rounded-full"
                                style={{ backgroundColor: `color-mix(in srgb, ${strong} 20%, white)` }}
                                aria-hidden
                            >
                                <span
                                    className="block h-full rounded-full transition-all"
                                    style={{
                                        width: `${pct}%`,
                                        backgroundColor: strong,
                                    }}
                                />
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Prev / centered title / Next */}
            {selectedPhase && (
                <div className="flex w-full items-center justify-between gap-3 px-0.5">
                    <button
                        type="button"
                        onClick={goPrev}
                        disabled={selectedIndex <= 0}
                        style={navBtnStyle(selectedIndex <= 0)}
                        aria-label={tr('page.myrebaby.prevphase')}
                    >
                        <ChevronLeft
                            className="h-6 w-6"
                            strokeWidth={2.75}
                            color="var(--highlight-color)"
                            aria-hidden
                        />
                    </button>

                    <div className="flex min-w-0 flex-1 flex-col items-center text-center">
                        <div className="flex flex-wrap items-center justify-center gap-2">
                            <span className="text-3xl leading-none">{selectedPhase.icon ?? '👶'}</span>
                            <span className="text-xl font-bold text-[var(--text-color)] sm:text-2xl">
                                {phaseTitle}
                            </span>
                            {currentPhaseId === selectedPhaseId && (
                                <span
                                    className="text-xs px-2 py-0.5 rounded-full font-semibold text-[var(--text-color)]"
                                    style={{
                                        backgroundColor: 'var(--phase-accent-soft)',
                                        border: '1px solid var(--phase-accent)',
                                    }}
                                >
                                    {tr('page.myrebaby.currentstage')}
                                </span>
                            )}
                        </div>
                        {selectedPhase.content?.displayNameKey && (
                            <p className="mt-0.5 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {tr(selectedPhase.content.displayNameKey)}
                            </p>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={goNext}
                        disabled={selectedIndex < 0 || selectedIndex >= phases.length - 1}
                        style={navBtnStyle(
                            selectedIndex < 0 || selectedIndex >= phases.length - 1
                        )}
                        aria-label={tr('page.myrebaby.nextphase')}
                    >
                        <ChevronRight
                            className="h-6 w-6"
                            strokeWidth={2.75}
                            color="var(--highlight-color)"
                            aria-hidden
                        />
                    </button>
                </div>
            )}
        </div>
    );
};

export default GrowthRoadmap;
